/**
 * Personalakte-Tab „Konto": username + Logto-Verknuepfung (readonly) und
 * Soft-Delete (Gate: loeschen) mit Bestaetigungs-Dialog.
 */

import { useState } from "react"
import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import { useNavigate } from "react-router-dom"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { useCan } from "@/lib/permissions"
import { api, apiError } from "@/services/api"
import type { EmployeeDetail } from "@/types"

export function AccountTab({ detail }: { detail: EmployeeDetail }) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const canDelete = useCan("loeschen")
  const [confirmOpen, setConfirmOpen] = useState(false)

  const softDelete = useMutation({
    mutationFn: () => api.employees.softDelete(detail.id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["employees"] })
      toast.success(`Mitarbeiter ${detail.displayName} gelöscht (Soft-Delete)`)
      navigate("/mitarbeiter")
    },
    onError: (e) => toast.error(apiError(e)),
  })

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card>
        <CardContent className="space-y-4 py-4">
          <h3 className="text-base font-semibold">Konto</h3>
          <div className="space-y-1.5">
            <Label>Benutzername</Label>
            <Input value={detail.username ?? ""} readOnly disabled />
          </div>
          <div className="space-y-1.5">
            <Label>Logto-User-ID</Label>
            <Input value={detail.logtoUserId ?? ""} readOnly disabled placeholder="— nicht verknüpft —" />
          </div>
          <div className="flex items-center gap-2 text-sm">
            <span className="text-muted-foreground">Account:</span>
            {detail.hasAccount ? (
              <Badge variant="outline" className="border-green-300 bg-green-100 text-green-800">
                verknüpft
              </Badge>
            ) : (
              <Badge variant="outline" className="border-gray-300 bg-gray-100 text-gray-700">
                kein Account
              </Badge>
            )}
          </div>
          {detail.primaryEmail && (
            <div className="space-y-1.5">
              <Label>Standard-E-Mail</Label>
              <Input value={detail.primaryEmail} readOnly disabled />
            </div>
          )}
        </CardContent>
      </Card>

      {canDelete && (
        <Card className="border-destructive/40">
          <CardContent className="space-y-3 py-4">
            <h3 className="text-base font-semibold text-destructive">Gefahrenzone</h3>
            <p className="text-sm text-muted-foreground">
              Soft-Delete: der Mitarbeiter wird aus allen Listen entfernt, die Daten bleiben
              fuer das Journal erhalten.
            </p>
            <Button variant="destructive" onClick={() => setConfirmOpen(true)}>
              Mitarbeiter löschen
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Mitarbeiter löschen?</DialogTitle>
            <DialogDescription>
              {detail.displayName} wird per Soft-Delete entfernt. Diese Aktion wird im Journal
              protokolliert.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmOpen(false)}>
              Abbrechen
            </Button>
            <Button
              variant="destructive"
              disabled={softDelete.isPending}
              onClick={() => softDelete.mutate()}
            >
              Endgültig löschen
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
